// One event, as a sentence a planner would say out loud.
//
// ── WHY THIS IS NOT IN History.tsx ──────────────────────────────────────────────
// Node's built-in type stripping runs `.ts` and refuses `.tsx`, so a test that imports the component
// file cannot run at all — "Unknown file extension .tsx". The wording IS the product here: a sentence
// that reads badly is a defect nothing throws on, and it deserves a test more than most code in this
// repo does. So the language lives in a plain module and the component renders it.
//
// The same shape as lib/panel.ts, for the same underlying reason: what a library and a component
// share belongs to neither of them.
import type { HistoryEntry } from '../api/planning.ts';

export const hhmm = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const dayLabel = (v: unknown): string => {
  if (typeof v !== 'string') return '?';
  const d = new Date(v + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? v
    : d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' });
};

const uur = (v: unknown): string => {
  const m = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(m)) return '?';
  const h = m / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1).replace('.', ',')} uur`;
};

/**
 * One event as a sentence a planner would say out loud.
 *
 * EXPORTED SO IT CAN BE TESTED without React or a database — the wording is the product here, and a
 * sentence that reads wrongly is a defect even though nothing threw.
 */
export function phrase(e: HistoryEntry): string {
  const wie = e.actorName ?? 'Iemand';
  const b = e.valueBefore ?? {};
  const a = e.valueAfter ?? {};
  const persoon = e.resourceName ? ` ${e.resourceName}` : '';

  if (!e.accepted) {
    // A REFUSAL IS PART OF THE STORY. Named without the server's reason: refusal text can mention a
    // person's competency, and this panel is read by people who should not see that.
    return `${wie} probeerde iets dat niet mocht — de planning weigerde het.`;
  }

  switch (e.eventType) {
    case 'project_added_to_planner':
      return `${wie} voegde deze werf toe aan de planner${e.reason ? ` — ${e.reason}` : ''}.`;
    case 'workpackage_created':
      return `${wie} maakte deze taak aan.`;
    case 'workpackage_renamed':
      return `${wie} hernoemde de taak naar “${String(a.title ?? '')}”.`;
    case 'task_category_changed':
      return `${wie} zette de soort werk op ${String(a.category ?? '?')}.`;
    case 'estimated_hours_changed':
      return `${wie} wijzigde de geschatte uren van ${uur(b.minutes)} naar ${uur(a.minutes)}.`;
    case 'allocation_created':
      return `${wie} plande${persoon} ${uur(a.minutes)} in op ${dayLabel(a.day)}.`;
    case 'allocation_hours_changed':
      return `${wie} wijzigde${persoon} van ${uur(b.minutes)} naar ${uur(a.minutes)}.`;
    case 'allocation_date_changed':
      return `${wie} verplaatste${persoon} van ${dayLabel(b.day)} naar ${dayLabel(a.day)}.`;
    case 'allocation_resource_changed':
      return `${wie} wisselde de medewerker om.`;
    case 'allocation_split':
      return `${wie} splitste de taak op.`;
    case 'allocation_copied':
      return `${wie} kopieerde een dag naar ${dayLabel(a.day)}.`;
    case 'allocation_deleted':
      return `${wie} haalde${persoon} van ${dayLabel(b.day ?? a.day)} weg.`;
    case 'assignment_added':
      return `${wie} zette${persoon} op deze taak.`;
    case 'assignment_removed':
      return `${wie} haalde${persoon} van deze taak.`;
    case 'status_changed':
      return `${wie} zette de status op ${String(a.state ?? a.status ?? '?')}.`;
    case 'capacity_warning_overridden':
      return `${wie} plande bewust door ondanks een overbezetting.`;
    case 'conflict_resolved':
      return `${wie} loste een conflict op.`;
    case 'undo_performed':
      return `${wie} maakte de vorige wijziging ongedaan.`;
    case 'planner_suggestion_accepted':
      return `${wie} nam een voorstel over.`;
    case 'planner_suggestion_rejected':
      return `${wie} legde een voorstel naast zich neer.`;
    default:
      // NEVER A RAW EVENT TYPE ON SCREEN. An unknown event still gets a sentence, because a planner
      // reading `allocation_frobnicated` learns nothing and loses trust in the whole panel.
      return `${wie} wijzigde iets aan deze taak.`;
  }
}
